import { Component, OnDestroy, OnInit } from "@angular/core"
import { NgSwitch, NgClass, NgSwitchCase, AsyncPipe, CommonModule } from "@angular/common"
import { ToastService } from "src/app/services"
import { Observable, Subscription } from "rxjs"
import {
    Column,
    ContentType,
    ModifyApplication,
    ModifyApplicationKeysObject,
    EmptyModifyApplicationErrorsObject,
    isValidEmail,
    setErrors,
    FileContent,
    FileContentFactory,
    Address,
    applicationStateNames,
    ModifyObject,
    ModifyObjectFactory,
    ModifyLandlordData,
    resizeFileContents,
} from "../../models"
import { ContextService, KngService, LoadingService } from "../../services"
import {
    AddressInputComponent,
    AdultFamilyMemberComponent,
    BooleanInputComponent,
    ConfirmDialogComponent,
    DropdownInputComponent,
    FileInputComponent,
    HeaderComponent,
    NumberInputComponent,
    TableComponent,
    TextInputComponent,
} from "../../components"
import { ModifyAdultFamilyMember, ModifyAdultFamilyMemberFactory, resizeAdultFamilyMembers } from "../../models/kng/adultFamilyMember.model"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatDialog } from "@angular/material/dialog"
@Component({
    selector: "valben-kng",
    templateUrl: "./kng.page.html",
    styles: [`
        :host {
            width: 100%;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        MatProgressSpinnerModule,
        HeaderComponent,
        NgSwitch,
        NgClass,
        CommonModule,
        NgSwitchCase,
        TableComponent,
        BooleanInputComponent,
        FileInputComponent,
        AsyncPipe,
        TextInputComponent,
        NumberInputComponent,
        DropdownInputComponent,
        AdultFamilyMemberComponent
    ],
})
export class KngPage implements OnInit, OnDestroy {
    tableValues: any[] = []

    resultSubscription: Subscription | undefined = new Subscription()
    itemsSubscription: Subscription | undefined = new Subscription()
    editItemObservable: () => Observable<any> | undefined = () =>
        this._contextService.getEditKngSubject()?.asObservable()
    updateEditName: (value: string) => void
    updateEditSurname: (value: string) => void
    updateEditOib: (value: string) => void
    updateEditResidenceAddress: (value: Address) => void
    updateEditPhoneNumber: (value: string) => void
    updateEditEmail: (value: string) => void
    updateEditNumberOfFamilyMembers: (value: number) => void
    updateEditEmployeeMonthlyIncome: (value: number) => void
    updateEditIsSingleParent: (value: boolean) => void
    updateEditNumberOfChildren: (value: number) => void
    updateEditCurrentAccomodation: (value: string) => void
    updateEditEmployeePropertyDocument: (value: FileContent) => void
    updateEditOwnsPropertyCloserThan30Km: (value: boolean) => void
    updateEditOwnsPropertyFurtherThan30Km: (value: boolean) => void
    updateEditOwnsLeaseAgreement: (value: boolean) => void
    updateEditLookingForApartment: (value: boolean) => void
    updateEditConsentsToPrivacyPolicy: (value: boolean) => void
    updateEditAdultFamilyMember: (payload: { index: number; member: ModifyAdultFamilyMember }) => void
    updateEditMinorFamilyMemberBirthCertificateAt: (index: number) => (file: FileContent) => void
    updateEditSingleParentConfirmation: (value: FileContent) => void
    updateEditResidenceCertificate: (value: FileContent) => void

    modifyKngKeys = ModifyApplicationKeysObject()
    currentEditItem: () => ModifyApplication

    editFormType: string = ""
    kngKey: string = ""

    loading$ = this._loadingService.kngLoading()
    mErrors: { [key: string]: boolean } = EmptyModifyApplicationErrorsObject()
    refresh = () => this._contextService.resetKng()

    numberOfFamilyMembers: number = 0
    familyMemberIndexes: number[] = []
    numberOfChildren: number = 0
    isSingleParent: boolean = false
    ownsLeaseAgreement: boolean = false

    currentAccomodationOptions = [
        { label: 'U najmu', value: 'U najmu' },
        { label: 'Vlastita nekretnina', value: 'Vlastita nekretnina' },
        { label: 'Nekretnina čiji je vlasnik član uže obitelji', value: 'Nekretnina čiji je vlasnik član uže obitelji' },
        { label: 'Kod roditelja', value: 'Kod roditelja' },
        { label: 'Kod poslodavca (Valamar Riviera / Imperial Riviera)', value: 'Kod poslodavca (Valamar Riviera / Imperial Riviera)' },
        { label: 'Ostalo', value: 'Ostalo' },
    ];

    cols: Column[] = [
        { field: "mbrad", header: "MBRAD", bodyType: "text", filterType: "text", filterMode: "contains", alignLeft: true },
        { field: "name", header: "Ime", bodyType: "text", filterType: "text", filterMode: "contains", alignLeft: true },
        { field: "surname", header: "Prezime", bodyType: "text", filterType: "text", sortable: true, filterMode: "contains", alignLeft: true },
        { field: "mobile", header: "Mobitel", bodyType: "text", filterType: "text", pairWith: "email" },
        { field: "email", header: "E-mail", bodyType: "text", filterType: "text" },
        { field: "createdByUser", header: "Kreiranje", bodyType: "text", filterType: "text", filterMode: "contains", pairWith: "modifiedByUser" },
        { field: "modifiedByUser", header: "Zadnja izmjena", bodyType: "text", filterType: "text", filterMode: "contains", prefix: "↳" },
        { field: "createdDate", header: "Datum kreiranja", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true, pairWith: "modifiedDate" },
        { field: "modifiedDate", header: "Datum izmjene", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true },
        { field: "stateLabel", header: "Status", bodyType: "text", filterType: "states", filterMode: "contains", alignLeft: true, pairWith: "version" },
        { field: "version", header: "Verzija", bodyType: "number", filterType: "numeric", sortable: true, prefix: "v" },
        { field: "superiorEvaluationData", header: "Procjena nadređenog", bodyType: "kngEvaluation", icon: "assignment_ind" },
        { field: "landlordData", header: "Podaci o najmodavcu", bodyType: "landlordData", icon: "real_estate_agent" },
    ]

    contentType: ContentType = ContentType.SEARCH

    editEnabled = false

    setContentType(value: string) {
        switch (value) {
            case "Search":
                if (!(this.contentType === ContentType.SEARCH)) {
                    this.contentType = ContentType.SEARCH
                }
                break
            case "Edit":
                if (!(this.contentType === ContentType.EDIT)) {
                    this.contentType = ContentType.EDIT
                }
                break
            default:
                if (!(this.contentType === ContentType.SEARCH)) {
                    this.contentType = ContentType.SEARCH
                }
                break
        }
    }

    constructor(
        private _messageService: ToastService,
        private _dialog: MatDialog,
        private _contextService: ContextService,
        private _loadingService: LoadingService,
        private _kngService: KngService,
    ) {
        this.editFormType = this._contextService.getEditKey()
        this.kngKey = this._contextService.getKngKey()

        this.updateEditName = (value: string) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.Name, value, this.editFormType)
        }
        this.updateEditSurname = (value: string) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.Surname, value, this.editFormType)
        }
        this.updateEditOib = (value: string) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.OIB, value, this.editFormType)
        }
        this.updateEditResidenceAddress = (value: Address) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.ResidenceAddress, value, this.editFormType)
        }
        this.updateEditPhoneNumber = (value: string) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.PhoneNumber, value, this.editFormType)
        }
        this.updateEditEmail = (value: string) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.Email, value, this.editFormType)
        }
        this.updateEditNumberOfFamilyMembers = (value: number) => {
            if (value < 0 || value > 20) {
                this._messageService.add({
                    severity: 'error',
                    summary: 'Invalid Input',
                    detail: 'Number of family members must be between 0 and 20.',
                    life: 10000,
                })
                this._contextService.setItem(this.kngKey, this.modifyKngKeys.NumberOfFamilyMembers, 0, this.editFormType)
                return
            }
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.NumberOfFamilyMembers, value, this.editFormType)
            const resized = resizeAdultFamilyMembers(this.currentEditItem()?.adultFamilyMembers ?? [], value)
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.AdultFamilyMembers, resized, this.editFormType)
        }
        this.updateEditEmployeeMonthlyIncome = (value: number) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.EmployeeMonthlyIncome, value, this.editFormType)
        }
        this.updateEditIsSingleParent = (value: boolean) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.IsSingleParent, value, this.editFormType)
        }
        this.updateEditNumberOfChildren = (value: number) => {
            if (value < 0 || value > 20) {
                this._messageService.add({
                    severity: 'error',
                    summary: 'Invalid Input',
                    detail: 'Number of kids must be between 0 and 20.',
                    life: 10000,
                });
                this._contextService.setItem(this.kngKey, this.modifyKngKeys.NumberOfChildren, 0, this.editFormType);
                return;
            }
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.NumberOfChildren, value, this.editFormType)
            const resized = resizeFileContents(
                this.currentEditItem()?.minorFamilyMembersBirthCertificates ?? [],
                value,
            )
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.MinorFamilyMembersBirthCertificates, resized, this.editFormType)
        }
        this.updateEditCurrentAccomodation = (value: string) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.CurrentAccomodation, value, this.editFormType)
        }
        this.updateEditEmployeePropertyDocument = (value: FileContent) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.EmployeePropertyDocument, value, this.editFormType)
        }
        this.updateEditOwnsPropertyCloserThan30Km = (value: boolean) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.OwnsPropertyCloserThan30Km, value, this.editFormType)
        }
        this.updateEditOwnsPropertyFurtherThan30Km = (value: boolean) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.OwnsPropertyFurtherThan30Km, value, this.editFormType)
        }
        this.updateEditOwnsLeaseAgreement = (value: boolean) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.OwnsLeaseAgreement, value, this.editFormType)
        }
        this.updateEditLookingForApartment = (value: boolean) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.LookingForApartment, value, this.editFormType)
        }
        this.updateEditConsentsToPrivacyPolicy = (value: boolean) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.ConsentsToPrivacyPolicy, value, this.editFormType)
        }
        this.updateEditAdultFamilyMember = (payload: { index: number; member: ModifyAdultFamilyMember }) => {
            const current = this.currentEditItem()?.adultFamilyMembers ?? []
            const updated = [...current]
            while (updated.length <= payload.index) {
                updated.push(ModifyAdultFamilyMemberFactory.create())
            }
            updated[payload.index] = payload.member
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.AdultFamilyMembers, updated, this.editFormType)
        }
        this.updateEditMinorFamilyMemberBirthCertificateAt = (index: number) => (file: FileContent) => {
            const current = this.currentEditItem()?.minorFamilyMembersBirthCertificates ?? []
            const updated = [...current]
            while (updated.length <= index) {
                updated.push(FileContentFactory.create())
            }
            updated[index] = file
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.MinorFamilyMembersBirthCertificates, updated, this.editFormType)
        }
        this.updateEditSingleParentConfirmation = (value: FileContent) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.SingleParentConfirmation, value, this.editFormType)
        }
        this.updateEditResidenceCertificate = (value: FileContent) => {
            this._contextService.setItem(this.kngKey, this.modifyKngKeys.ResidenceCertificate, value, this.editFormType)
        }

        this.currentEditItem = () => this._contextService.getEditKngSubject()?.value
    }

    ngOnInit(): void {
        this.itemsSubscription = this._contextService.getKngSubject()?.subscribe(applications => {
            this.tableValues = applications.map(x => ({
                ...x,
                stateLabel: applicationStateNames[x.state],
                createdDate: new Date(x.createdDate),
                modifiedDate: new Date(x.modifiedDate),
            }))
        })
        this.resultSubscription = this._contextService.getKngResultSubject()?.subscribe(res => {
            if (res !== null) {
                if (res.formType === this.editFormType && res.success) {
                    this.editEnabled = false
                    this.setContentType("Search")
                }
                if (res.formType === this.editFormType) {
                    setErrors(res.errorFields, this.mErrors)
                }
                this._messageService.add({
                    severity: res.error ? "error" : "success",
                    summary: res.error ? "Error" : "Success",
                    detail: res.message,
                    life: 10000,
                })
            }
        })
        this._contextService.getEditKngSubject()?.subscribe((res: any) => {
            if (res === null) return
            if (res[this.modifyKngKeys.NumberOfFamilyMembers] !== this.numberOfFamilyMembers) {
                this.numberOfFamilyMembers = res[this.modifyKngKeys.NumberOfFamilyMembers] || 0
                this.familyMemberIndexes = Array.from({ length: this.numberOfFamilyMembers }, (_, i) => i)
            }
            if (res[this.modifyKngKeys.NumberOfChildren] !== this.numberOfChildren) {
                this.numberOfChildren = res[this.modifyKngKeys.NumberOfChildren] || 0
            }
            if (res[this.modifyKngKeys.IsSingleParent] !== this.isSingleParent) {
                this.isSingleParent = res[this.modifyKngKeys.IsSingleParent] || false
            }
            if (res[this.modifyKngKeys.OwnsLeaseAgreement] !== this.ownsLeaseAgreement) {
                this.ownsLeaseAgreement = res[this.modifyKngKeys.OwnsLeaseAgreement] || false
            }

            if ((res[this.modifyKngKeys.AdultFamilyMembers]?.length ?? 0) !== this.numberOfFamilyMembers) {
                const currentLength = res[this.modifyKngKeys.AdultFamilyMembers]?.length ?? 0
                const desiredLength = this.numberOfFamilyMembers
                if (currentLength < desiredLength) {
                    const itemsToAdd = desiredLength - currentLength
                    const newItems = new Array(itemsToAdd).fill(null).map(() => ModifyAdultFamilyMemberFactory.create())
                    res[this.modifyKngKeys.AdultFamilyMembers] = (res[this.modifyKngKeys.AdultFamilyMembers] ?? []).concat(newItems)
                } else if (currentLength > desiredLength) {
                    res[this.modifyKngKeys.AdultFamilyMembers] = (res[this.modifyKngKeys.AdultFamilyMembers] ?? []).slice(0, desiredLength)
                }
            }
            if ((res[this.modifyKngKeys.MinorFamilyMembersBirthCertificates]?.length ?? 0) !== this.numberOfChildren) {
                const currentLength = res[this.modifyKngKeys.MinorFamilyMembersBirthCertificates]?.length ?? 0
                const desiredLength = this.numberOfChildren
                if (currentLength < desiredLength) {
                    const itemsToAdd = desiredLength - currentLength
                    const newItems = new Array(itemsToAdd).fill(null).map(() => FileContentFactory.create())
                    res[this.modifyKngKeys.MinorFamilyMembersBirthCertificates] = (res[this.modifyKngKeys.MinorFamilyMembersBirthCertificates] ?? []).concat(newItems)
                } else if (currentLength > desiredLength) {
                    res[this.modifyKngKeys.MinorFamilyMembersBirthCertificates] = (res[this.modifyKngKeys.MinorFamilyMembersBirthCertificates] ?? []).slice(0, desiredLength)
                }
            }
        })
    }

    ngOnDestroy(): void {
        this.tableValues = []
        this.resultSubscription?.unsubscribe()
        this.itemsSubscription?.unsubscribe()
    }

    editOnClick(masterId: string) {
        this._contextService.setCurrentEditKng(masterId)
        this.editEnabled = true
        this.setContentType("Edit")
    }

    modify() {
        const email = this.currentEditItem()?.email ?? ""
        if (!isValidEmail(email)) {
            this.mErrors[this.modifyKngKeys.Email] = true
            this._messageService.add({
                severity: "error",
                summary: "Neispravan email",
                detail: "Unesite ispravan email format.",
                life: 10000,
            })
            return
        }
        this.mErrors[this.modifyKngKeys.Email] = false
        this._contextService.modifyKng()
    }

    cancel() {
        const masterId = this.currentEditItem().masterId
        this._contextService.cancelKngApplication(masterId)
        this.editEnabled = true
        this.setContentType("Search")
    }

    confirmCancel(_event: Event) {
        this._dialog
            .open(ConfirmDialogComponent, {
                width: "35rem",
                data: {
                    header: "Potvrdi otkazivanje",
                    message: "Želite li stvarno otkazati prijavu?",
                    confirmText: "Da",
                    cancelText: "Odustani",
                },
            })
            .afterClosed()
            .subscribe(confirmed => {
                if (confirmed) this.cancel()
            })
    }

    back(edit?: boolean) {
        if (edit) {
            this._contextService.setCurrentEditKng(undefined)
            this.editEnabled = false
        }
        this.setContentType("Search")
    }

    saveLandlordData(event: { masterId: string, version: number, data: ModifyLandlordData }): void {
        const landlordDataPayload = {
            name: event.data.name,
            surname: event.data.surname,
            phoneNumber: event.data.phoneNumber,
            email: event.data.email,
            oib: event.data.oib,
            residenceAddress: event.data.residenceAddress,
            propertyRooms: event.data.propertyRooms,
            areaOfProperty: event.data.areaOfProperty,
            iban: event.data.iban,
            swift: event.data.swift,
            startRentDate: event.data.startRentDate,
            endRentDate: event.data.endRentDate,
            isApartmentOfLegalEntity: event.data.isApartmentOfLegalEntity,
            rentalPropertyAddress: event.data.rentalPropertyAddress,
            monthlyRent: event.data.monthlyRent,
            consentForm: event.data.consentForm ?? FileContentFactory.create(),
            catastarKO: event.data.catastarKO,
            catastarCE: event.data.catastarCE,
            ownershipCertificate: event.data.ownershipCertificate ?? FileContentFactory.create(),
            submitted: event.data.submitted,
        }

        const modifyObject: ModifyObject = ModifyObjectFactory.create(
            [
                {
                    op: "replace",
                    path: "/landlordData",
                    value: landlordDataPayload,
                },
            ],
            event.version
        )

        this._contextService.editKng(event.masterId, modifyObject)

        this._messageService.add({
            severity: "info",
            summary: "Info",
            detail: "Spremanje podataka o najmodavcu...",
            life: 3000,
        })
    }
}
